/**
 * Minimal Intuit Interchange Format (.IIF) parser for QuickBooks exports.
 * IIF is tab-delimited. Each section starts with a header line beginning
 * with `!` (e.g. `!CUST	NAME	EMAIL	...`) defining the column order; data
 * rows share the first-column "record type" without the `!`.
 *
 * Supported record types for import:
 *   !CUST — customers  → Person (first name / last name / email / phone)
 *   !TRNS + !SPL — transactions (invoices/payments). Each TRNS starts a
 *     transaction; zero or more SPL lines are its splits; ENDTRNS closes.
 *
 * This is a best-effort parser — IIF is notoriously loose. Unknown
 * columns are captured in `raw` for the UI to show as context.
 */

export type IIFSectionType = 'CUST' | 'TRNS' | 'SPL' | 'ENDTRNS' | string;

export interface IIFRow {
	type: IIFSectionType;
	raw: Record<string, string>;
}

export interface IIFParse {
	customers: IIFRow[];
	vendors: IIFRow[];
	accounts: IIFRow[];
	invItems: IIFRow[];
	transactions: Array<{ header: IIFRow; splits: IIFRow[] }>;
	otherRows: IIFRow[];
	errors: string[];
}

/**
 * Split one IIF line into cells, quote-aware. IIF is tab-delimited, but
 * QuickBooks wraps any field containing a special char (comma, tab, …) in double
 * quotes, escaping inner quotes by doubling them. A naive `split('\t')` both
 * leaves the wrapping quotes on the cell (corrupting a name like
 * `"62WC - (CLOSED, 4825PC) …"` → leading `"` on account_number) AND splits a
 * tab that lives *inside* a quoted field. This tokenizer:
 *   - toggles quote state on `"`, treating `""` inside quotes as a literal `"`,
 *   - only breaks on a tab when NOT inside quotes,
 *   - drops the wrapping quotes (an unterminated quote just runs to end-of-line,
 *     so a truncated `"7 - Whalen` yields `7 - Whalen`, not `"7 - Whalen`).
 */
const splitIifCells = (line: string): string[] => {
	const cells: string[] = [];
	let cur = '';
	let inQuotes = false;
	for (let i = 0; i < line.length; i++) {
		const ch = line[i];
		if (inQuotes) {
			if (ch === '"') {
				if (line[i + 1] === '"') { cur += '"'; i++; } // escaped quote
				else inQuotes = false; // closing quote
			} else { cur += ch; }
		} else if (ch === '"') {
			inQuotes = true; // opening quote
		} else if (ch === '\t') {
			cells.push(cur); cur = '';
		} else { cur += ch; }
	}
	cells.push(cur);
	return cells;
};

export function parseIIF(text: string): IIFParse {
	const out: IIFParse = { customers: [], vendors: [], accounts: [], invItems: [], transactions: [], otherRows: [], errors: [] };
	// Header column names keyed by record type: map from "!CUST" → ["NAME", "EMAIL", ...]
	const headers: Record<string, string[]> = {};
	const lines = text.split(/\r?\n/);
	let currentTxn: { header: IIFRow; splits: IIFRow[] } | null = null;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		if (!line.trim()) continue;
		const cells = splitIifCells(line);
		const first = cells[0];

		if (first.startsWith('!')) {
			// Header line. Column 0 is the type tag, rest are column names.
			const type = first.slice(1);
			headers[type] = cells.slice(1);
			continue;
		}

		const type = first as IIFSectionType;
		const cols = headers[type];
		if (!cols) {
			// Unknown record (no matching header); skip silently but keep in raw.
			out.otherRows.push({ type, raw: { _line: line } });
			continue;
		}
		const raw: Record<string, string> = {};
		for (let j = 0; j < cols.length; j++) {
			raw[cols[j]] = cells[j + 1] ?? '';
		}
		const row: IIFRow = { type, raw };

		if (type === 'CUST') {
			out.customers.push(row);
		} else if (type === 'VEND') {
			out.vendors.push(row);
		} else if (type === 'ACCNT') {
			out.accounts.push(row);
		} else if (type === 'INVITEM') {
			out.invItems.push(row);
		} else if (type === 'TRNS') {
			currentTxn = { header: row, splits: [] };
			out.transactions.push(currentTxn);
		} else if (type === 'SPL') {
			if (currentTxn) currentTxn.splits.push(row);
			else out.errors.push(`Line ${i + 1}: SPL without TRNS`);
		} else if (type === 'ENDTRNS') {
			currentTxn = null;
		} else {
			out.otherRows.push(row);
		}
	}

	return out;
}

// Small helpers for mapping the raw string cells to our domain shape.

export const iifDateToMs = (s: string | undefined): number | null => {
	if (!s) return null;
	// QB uses M/D/YYYY or MM/DD/YYYY
	const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
	if (!m) return null;
	const [_, mm, dd, yy] = m;
	const year = yy.length === 2 ? 2000 + Number(yy) : Number(yy);
	// Business date — midnight UTC, not local midnight (which is off-grid and lands on the
	// previous day east of Greenwich). Same as the CSV parsers (#447).
	const ms = Date.UTC(year, Number(mm) - 1, Number(dd));
	return isFinite(ms) ? ms : null;
};

export const iifMoneyToCents = (s: string | undefined): number | null => {
	if (!s) return null;
	// QB values are decimal; payments come in as positive while TRNS AMOUNT
	// for deposits is negative — we let the caller interpret sign.
	const n = Number(String(s).replace(/[,"]/g, ''));
	if (!isFinite(n)) return null;
	return Math.round(n * 100);
};
